/**
 * Which currency a visitor is shown, and the control that changes it.
 * @file src/web/shared/currency.tsx
 *
 * bloomneo bills through two gateways: Creem, as merchant of record, in US
 * dollars for the rest of the world, and Razorpay in rupees for India. A
 * package therefore has two prices — not one price converted, two prices set
 * — and every surface that quotes one has to agree with the others about
 * which it is quoting.
 *
 * ── Why the default comes from the clock and not from the IP ───────────────
 *
 * The obvious implementation is to geolocate the address. It needs either a
 * licensed database kept up to date on the server, or a third-party lookup on
 * the path of every page load — a dependency that can be slow, can be down,
 * and sees every visitor's address.
 *
 * `Intl.DateTimeFormat().resolvedOptions().timeZone` answers the same
 * question instantly, offline, from something the browser already knows. It
 * is also the better signal: a timezone follows the device, so somebody in
 * Bengaluru behind a US VPN still reads Asia/Kolkata, where their IP says
 * California. IP geolocation is wrong in exactly the cases people notice.
 *
 * Neither signal is authoritative, which is the real point — so this is only
 * a DEFAULT, and the toggle beside it is the actual answer. A choice is
 * remembered, because somebody who has corrected us once should not have to
 * do it on every page.
 *
 * None of this decides what anyone is charged. The gateway and the amount are
 * settled server-side when a checkout is created; this chooses what is shown.
 */
import * as React from 'react';

export type Currency = 'USD' | 'INR';

const STORAGE_KEY = 'bloomneo:currency';

/**
 * India's zones, as the browser spells them. Asia/Calcutta is the older name
 * and is still what some devices report, so both are listed — matching only
 * the current spelling would quietly bill a chunk of India in dollars.
 */
const INDIA_ZONES = new Set(['Asia/Kolkata', 'Asia/Calcutta']);

function guessFromTimeZone(): Currency {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return INDIA_ZONES.has(zone) ? 'INR' : 'USD';
  } catch {
    /* Some embedded browsers have no Intl. Dollars is the safer miss: a
       customer shown the international price who should have seen rupees is
       mildly annoyed, where the reverse undercharges. */
    return 'USD';
  }
}

function readStored(): Currency | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'USD' || v === 'INR' ? v : null;
  } catch {
    /* Private windows and blocked site data both throw on access rather than
       returning null, so every read is wrapped. */
    return null;
  }
}

/**
 * The currency to display, and a setter that remembers the choice.
 *
 * Starts at USD rather than at the guess, deliberately: the server renders
 * this page too, and a first paint that disagrees with the client's guess
 * produces a flash of the wrong price. The guess is applied in an effect,
 * after mount, where the server and the browser cannot disagree.
 */
export function useCurrency(): {
  currency: Currency;
  setCurrency: (c: Currency) => void;
  /** False until the guess has run, for callers that would rather show nothing. */
  resolved: boolean;
} {
  const [currency, setState] = React.useState<Currency>('USD');
  const [resolved, setResolved] = React.useState(false);

  React.useEffect(() => {
    setState(readStored() ?? guessFromTimeZone());
    setResolved(true);
  }, []);

  const setCurrency = React.useCallback((c: Currency) => {
    setState(c);
    try { localStorage.setItem(STORAGE_KEY, c); } catch { /* not worth failing over */ }
  }, []);

  return { currency, setCurrency, resolved };
}

/**
 * Format a minor-unit amount the way its own currency is written.
 *
 * Rupees use the Indian digit grouping — ₹40,000 but ₹1,20,000 — which
 * `en-IN` gets right and `en-US` does not. Getting it wrong is a small thing
 * that reads as carelessness to exactly the audience it is shown to.
 */
export function money(minor: string | number | null | undefined, currency: Currency): string {
  if (minor === null || minor === undefined || minor === '') return '—';
  const n = Number(minor) / 100;
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(n);
}

/**
 * Pick the right price off a plan row, or null when it is not sold here.
 *
 * Null is a real answer and not a zero: a package with no rupee price is one
 * India cannot buy, and the caller is expected to say so rather than print a
 * converted guess or fall back to the dollar figure under a ₹ sign.
 */
export function priceFor(
  plan: { priceMinor?: string | null; inrPriceMinor?: string | null },
  currency: Currency,
): string | null {
  const raw = currency === 'INR' ? plan.inrPriceMinor : plan.priceMinor;
  return raw ? String(raw) : null;
}

/**
 * The toggle itself. Small, quiet, and always visible where a price is —
 * somebody shown the wrong currency needs to see the way to fix it without
 * hunting, and somebody shown the right one should barely notice it.
 */
export function CurrencyToggle({
  currency,
  onChange,
  className = '',
}: {
  currency: Currency;
  onChange: (c: Currency) => void;
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label="Display currency"
      className={`inline-flex items-center rounded-full border border-border bg-muted/50 p-0.5 text-xs ${className}`}
    >
      {(['USD', 'INR'] as const).map((c) => {
        const on = currency === c;
        return (
          <button
            key={c}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(c)}
            className={`cursor-pointer rounded-full px-2.5 py-1 font-medium transition-colors ${
              on ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {c === 'USD' ? '$ USD' : '₹ INR'}
          </button>
        );
      })}
    </div>
  );
}
